// Módulos (telas do menu) que um cargo pode acessar. As chaves são as mesmas
// do CHECK em job_roles.modules (migration 20261001000003).
export type ModuleKey =
  | 'insights' | 'projects' | 'price_simulator' | 'activities' | 'time'
  | 'product_spec' | 'finances' | 'commercial' | 'controls';

export const MODULES: Array<{ key: ModuleKey; label: string; sections: string[] }> = [
  { key: 'insights', label: 'Insights', sections: ['nankin-insights'] },
  { key: 'projects', label: 'Projetos', sections: ['projects', 'suppliers-clients-manager'] },
  // Opcional, liberado pelo admin cargo a cargo. Ficava dentro de Projetos e
  // qualquer cargo com Projetos abria o simulador. O item continua no submenu
  // de Projetos, então na prática pede os dois módulos.
  { key: 'price_simulator', label: 'Simulador de propostas', sections: ['price-simulator'] },
  {
    key: 'activities', label: 'Atividades',
    sections: ['my-activities', 'activities', 'activities-control', 'activities-calendar', 'activities-strategic',
      'tasks-unified', 'tasks', 'tasks-summary', 'tasks-insights', 'new-task', 'today', 'upcoming', 'overdue',
      'filters', 'completed'],
  },
  { key: 'time', label: 'Uso do tempo', sections: ['time', 'tracker', 'calendar', 'reports'] },
  {
    key: 'product_spec', label: 'Especificação de produto',
    sections: ['product-spec', 'product-spec-products', 'product-spec-services', 'product-spec-budget',
      'product-spec-service-types'],
  },
  {
    key: 'finances', label: 'Finanças',
    sections: ['finances', 'financial-dashboard', 'financial-movements', 'reconciliation', 'financial-projects',
      'financial-calendar', 'financial-reports', 'financial-registers'],
  },
  { key: 'commercial', label: 'Comercial', sections: ['commercial', 'sales-pipeline', 'clients'] },
  { key: 'controls', label: 'Controles', sections: ['controls', 'bank-accounts'] },
];

export const ALL_MODULE_KEYS: ModuleKey[] = MODULES.map((m) => m.key);

// Telas que só administrador vê (Gestão de acessos).
export const ADMIN_SECTIONS = ['user-management', 'team-costs'];

const SECTION_TO_MODULE = new Map<string, ModuleKey>(
  MODULES.flatMap((m) => m.sections.map((s) => [s, m.key] as [string, ModuleKey]))
);

export function moduleForSection(section: string): ModuleKey | null {
  return SECTION_TO_MODULE.get(section) ?? null;
}

export interface ModuleAccess {
  loading: boolean;
  isAdmin: boolean;
  /** Módulos do cargo; null = pessoa sem cargo (vê todos os módulos). */
  modules: ModuleKey[] | null;
}

// Esconder tela é conveniência de interface; quem protege os dados é a RLS.
// Por isso, enquanto carrega, não bloqueia (evita piscar "Sem acesso").
export function canAccessSection(access: ModuleAccess, section: string): boolean {
  if (access.loading || access.isAdmin) return true;
  if (ADMIN_SECTIONS.includes(section)) return false;
  const module = moduleForSection(section);
  if (!module || access.modules === null) return true;
  return access.modules.includes(module);
}

/** Primeira tela liberada, na ordem do menu; null se nenhum módulo estiver liberado. */
export function firstAllowedSection(access: ModuleAccess): string | null {
  for (const m of MODULES) {
    if (canAccessSection(access, m.sections[0])) return m.sections[0];
  }
  return null;
}
